/**
 * Утилиты для валидации форм с использованием Zod
 *
 * Использует Zod для консистентности с серверной валидацией
 */

import { z } from 'zod';
import type { CreateInviteFormData } from '@/types/invite';
import type { RSVPFormData } from '@/types/rsvp';
import {
  MIN_NAME_LENGTH,
  MAX_NAME_LENGTH,
  MAX_LOCATION_LENGTH,
  MIN_GUEST_COUNT,
  MAX_GUEST_COUNT,
  MAX_MESSAGE_LENGTH,
  MAX_GUEST_NAME_LENGTH,
} from '@/constants/validation';

type ValidationResult<T> = {
  success: true;
  data: T;
  error?: never;
} | {
  success: false;
  data?: never;
  error: string;
};

/**
 * Схема валидации для создания приглашения (клиентская)
 * Синхронизирована с серверной схемой в worker/src/validation/inviteSchema.ts
 */
const createInviteFormSchema = z.object({
  brideName: z
    .string()
    .min(MIN_NAME_LENGTH, 'Имя невесты обязательно для заполнения')
    .max(MAX_NAME_LENGTH, `Имя невесты не должно превышать ${MAX_NAME_LENGTH} символов`)
    .trim(),
  groomName: z
    .string()
    .min(MIN_NAME_LENGTH, 'Имя жениха обязательно для заполнения')
    .max(MAX_NAME_LENGTH, `Имя жениха не должно превышать ${MAX_NAME_LENGTH} символов`)
    .trim(),
  weddingDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Неверный формат даты. Используйте формат YYYY-MM-DD')
    .refine(
      (date) => {
        const parsedDate = new Date(date);
        return !Number.isNaN(parsedDate.getTime());
      },
      { message: 'Неверная дата. Проверьте правильность введенной даты' },
    )
    .refine(
      (date) => {
        const parsedDate = new Date(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return parsedDate >= today;
      },
      { message: 'Дата свадьбы не может быть в прошлом' },
    ),
  weddingLocation: z
    .string()
    .min(MIN_NAME_LENGTH, 'Место проведения обязательно для заполнения')
    .max(MAX_LOCATION_LENGTH, `Место проведения не должно превышать ${MAX_LOCATION_LENGTH} символов`)
    .trim(),
  template: z.enum(['classic', 'modern', 'romantic', 'vintage', 'minimal', 'garden', 'charming-natural']).optional(),
});

/**
 * Схема валидации для RSVP формы (клиентская)
 * Синхронизирована с серверной схемой в worker/src/validation/rsvpSchema.ts
 */
const createRSVPFormSchema = z.object({
  response: z.enum(['yes', 'no'], {
    required_error: 'Необходимо выбрать ответ',
    invalid_type_error: 'Ответ должен быть "yes" или "no"',
  }),
  guestCount: z
    .number()
    .int('Количество гостей должно быть целым числом')
    .min(0, 'Количество гостей не может быть отрицательным')
    .max(MAX_GUEST_COUNT, `Количество гостей не может превышать ${MAX_GUEST_COUNT}`),
  guestNames: z
    .array(
      z.string()
        .trim()
        .max(
          MAX_GUEST_NAME_LENGTH,
          `Имя гостя не должно превышать ${MAX_GUEST_NAME_LENGTH} символов`,
        ),
    )
    .optional()
    .default([]),
  message: z
    .string()
    .max(MAX_MESSAGE_LENGTH, `Сообщение не должно превышать ${MAX_MESSAGE_LENGTH} символов`)
    .trim()
    .optional(),
});

/**
 * Валидация формы создания приглашения с использованием Zod
 *
 * @param formData - Данные формы
 * @returns Результат валидации
 */
export const validateInviteForm = (
  formData: CreateInviteFormData,
): ValidationResult<CreateInviteFormData> => {
  const result = createInviteFormSchema.safeParse(formData);

  if (!result.success) {
    const errorMessage = result.error.errors.map((e) => e.message).join(', ');
    return {
      success: false,
      error: errorMessage,
    };
  }

  return {
    success: true,
    data: result.data,
  };
};

/**
 * Валидация формы RSVP с использованием Zod
 *
 * @param formData - Данные формы RSVP
 * @returns Результат валидации
 */
export const validateRSVPForm = (
  formData: RSVPFormData,
): ValidationResult<RSVPFormData> => {
  const result = createRSVPFormSchema.safeParse(formData);

  if (!result.success) {
    const errorMessage = result.error.errors.map((e) => e.message).join(', ');
    return {
      success: false,
      error: errorMessage,
    };
  }

  const parsed = result.data;

  // Дополнительная бизнес-логика валидации
  if (parsed.response === 'yes' && parsed.guestCount < MIN_GUEST_COUNT) {
    return {
      success: false,
      error: `При подтверждении присутствия необходимо указать количество гостей (минимум ${MIN_GUEST_COUNT})`,
    };
  }

  if (parsed.response === 'no' && parsed.guestCount !== 0) {
    return {
      success: false,
      error: 'При отказе количество гостей должно быть 0',
    };
  }

  return {
    success: true,
    data: parsed,
  };
};
